import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import {
    approveVerticalSeed,
    generateVerticalSeedDraft,
    getVerticalSeeds,
    runVerticalSeedBatch
} from "../services/verticalSeedService";
import { isSuperAdminUser } from "../services/authService";
import {
    getSeedRecommendations,
    runSeedLearning,
    generateSeedDraftFromRecommendation,
    materializeAllSeedImages,
    materializeSeedImages,
    generateSeedTestSites
} from "../services/seedLearningService";

import {
    getBlueprints,
    getBlueprint,
    getVerticalIntelligence
} from "../services/blueprintV2Service";

const defaultForm = {
    industry: "Education",
    subIndustry: "Sports Academy",
    primaryGoal: "Generate Leads",
    toneOfVoice: "Professional",
    brandStyle: "Modern",
    themeKey: "clean-light"
};

function getModuleIcon(key = "") {
    const value = key.toLowerCase();

    if (value.includes("product")) return "🛍️";
    if (value.includes("collection")) return "🧩";
    if (value.includes("program")) return "🎓";
    if (value.includes("coach")) return "🏅";
    if (value.includes("doctor")) return "🩺";
    if (value.includes("treatment")) return "💊";
    if (value.includes("practice")) return "⚖️";
    if (value.includes("team")) return "👥";
    if (value.includes("property")) return "🏢";
    if (value.includes("localit")) return "📍";
    if (value.includes("pricing")) return "💳";
    if (value.includes("feature")) return "⚡";
    if (value.includes("integration")) return "🔌";

    return "📦";
}

function getPriorityClass(priority = "") {
    const value = String(priority).toLowerCase();
    if (value === "high") return "high";
    if (value === "medium") return "medium";
    return "low";
}

function getRecommendationStatusLabel(status = "") {
    if (status === "Reviewed") return "Draft Created";
    if (status === "DraftFailed") return "Draft Failed";
    return status || "-";
}

function formatDate(value) {
    if (!value) return "-";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function normalizeSeedStatus(status) {
    const value = String(status ?? "").toLowerCase();

    if (value === "0" || value === "draft") return "draft";
    if (value === "1" || value === "active") return "active";
    if (value === "2" || value === "archived") return "archived";

    return value || "unknown";
}

function getSeedStatusLabel(status) {
    const value = normalizeSeedStatus(status);

    if (value === "draft") return "Draft";
    if (value === "active") return "Active";
    if (value === "archived") return "Archived";

    return value;
}

function getSeedStatusClass(status) {
    const value = normalizeSeedStatus(status);

    if (value === "active") return "active";
    if (value === "draft") return "draft";
    if (value === "archived") return "archived";

    return "neutral";
}

function buildSeedSearchText(seed) {
    return [
        seed.id,
        seed.seedKey,
        seed.industry,
        seed.subIndustry,
        seed.pattern,
        seed.templatePackKey,
        seed.status,
        seed.version,
        ...(seed.modules || []).map((m) => `${m.key} ${m.displayName} ${m.entityType}`)
    ]
        .join(" ")
        .toLowerCase();
}

export default function VerticalSeedsPage() {
    const isSuperAdmin = isSuperAdminUser();

    const [form, setForm] = useState(defaultForm);
    const [seeds, setSeeds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [approvingId, setApprovingId] = useState(null);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    const [batchRunning, setBatchRunning] = useState(false);
    const [batchResult, setBatchResult] = useState(null);
    const [selectedSeed, setSelectedSeed] = useState(null);

    const [recommendations, setRecommendations] = useState([]);
    const [learningRunning, setLearningRunning] = useState(false);
    const [generatingSeedId, setGeneratingSeedId] = useState(null);

    const [materializingAll, setMaterializingAll] = useState(false);
    const [materializingSeedId, setMaterializingSeedId] = useState(null);

    const [testGeneratingSeedId, setTestGeneratingSeedId] = useState(null);
    const [searchParams] = useSearchParams();

    const [seedSearch, setSeedSearch] = useState("");
    const [seedStatusFilter, setSeedStatusFilter] = useState("all");
    const [seedActiveFilter, setSeedActiveFilter] = useState("all");
    const [seedTemplateFilter, setSeedTemplateFilter] = useState("all");
    const [seedIndustryFilter, setSeedIndustryFilter] = useState("all");
    const [seedPage, setSeedPage] = useState(1);
    const [seedPageSize, setSeedPageSize] = useState(10);

    const [activeTab, setActiveTab] = useState("seeds");

    const [blueprints, setBlueprints] = useState([]);
    const [selectedBlueprint, setSelectedBlueprint] = useState(null);

    const [intelligenceRows, setIntelligenceRows] = useState([]);

    const [blueprintLoading, setBlueprintLoading] = useState(false);
    const [intelligenceLoading, setIntelligenceLoading] = useState(false);

    const [blueprintSearch, setBlueprintSearch] = useState("");

    const syncSelectedSeed = useCallback((items) => {
        setSelectedSeed((prev) => {
            if (!prev) return prev;
            const updated = (items || []).find((x) => x.id === prev.id);
            return updated || prev;
        });
    }, []);

    const loadBlueprints = async () => {
        try {
            setBlueprintLoading(true);

            const data = await getBlueprints();

            setBlueprints(data || []);
        }
        finally {
            setBlueprintLoading(false);
        }
    };

    const loadIntelligence = async () => {
        try {
            setIntelligenceLoading(true);

            const data = await getVerticalIntelligence();

            setIntelligenceRows(data || []);
        }
        finally {
            setIntelligenceLoading(false);
        }
    };

    const loadSeeds = useCallback(async () => {
        if (!isSuperAdmin) return;

        try {
            setLoading(true);
            setError("");

            const data = await getVerticalSeeds();
            const list = Array.isArray(data) ? data : [];

            setSeeds(list);
            syncSelectedSeed(list);
        } catch (err) {
            console.error(err);
            setError("Failed to load vertical seeds.");
        } finally {
            setLoading(false);
        }
    }, [isSuperAdmin, syncSelectedSeed]);

    const loadRecommendations = useCallback(async () => {
        try {
            const data = await getSeedRecommendations();
            setRecommendations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        loadSeeds();
    }, [loadSeeds]);

    useEffect(() => {
        loadRecommendations();
    }, [loadRecommendations]);

    useEffect(() => {
        loadBlueprints();
        loadIntelligence();
    }, []);

    useEffect(() => {
        const seedId = searchParams.get("seedId");
        if (!seedId || !Array.isArray(seeds) || seeds.length === 0) return;

        const match = seeds.find((seed) => String(seed.id) === String(seedId));
        if (match) setSelectedSeed(match);
    }, [searchParams, seeds]);

    const updateField = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRunBatch = async () => {
        try {
            setBatchRunning(true);
            setMessage("");
            setError("");
            setBatchResult(null);

            const result = await runVerticalSeedBatch({
                skipExisting: true,
                maxCount: 50
            });

            setBatchResult(result);
            setMessage(
                `Batch completed. Created: ${result.created}, Skipped: ${result.skipped}, Failed: ${result.failed}`
            );

            await loadSeeds();
        } catch (err) {
            console.error(err);
            setError("Failed to run vertical seed batch.");
        } finally {
            setBatchRunning(false);
        }
    };

    const filteredBlueprints = useMemo(() => {
        const q = blueprintSearch.toLowerCase().trim();

        if (!q)
            return blueprints;

        return blueprints.filter(x =>
            `${x.verticalKey}
         ${x.industry}
         ${x.subIndustry || ""}`
                .toLowerCase()
                .includes(q)
        );
    }, [blueprints, blueprintSearch]);

    const handleGenerateSeedTestSites = async (seed) => {
        if (!seed?.id) return;

        try {
            setTestGeneratingSeedId(seed.id);
            setMessage("");
            setError("");

            const result = await generateSeedTestSites(seed.id, 2);

            setMessage(
                `Seed test sites queued for ${seed.industry}${seed.subIndustry ? ` / ${seed.subIndustry}` : ""}. ${result?.summary || ""}`
            );
        } catch (err) {
            console.error(err);
            setError("Failed to generate seed test sites.");
        } finally {
            setTestGeneratingSeedId(null);
        }
    };

    const handleRunLearning = async () => {
        try {
            setLearningRunning(true);
            setMessage("");
            setError("");

            await runSeedLearning();
            await loadRecommendations();

            setMessage("Seed learning recommendations updated.");
        } catch (err) {
            console.error(err);
            setError("Failed to run seed learning.");
        } finally {
            setLearningRunning(false);
        }
    };

    const handleGenerateSeedDraftFromRecommendation = async (id) => {
        try {
            setGeneratingSeedId(id);
            setMessage("");
            setError("");

            const result = await generateSeedDraftFromRecommendation(id);

            await loadSeeds();
            await loadRecommendations();

            setMessage(
                `Seed draft vNext generated from recommendation. Seed ID: ${result?.createdSeedId || result?.seedId || "-"}`
            );
        } catch (err) {
            console.error(err);
            setError("Failed to generate seed draft from recommendation.");
        } finally {
            setGeneratingSeedId(null);
        }
    };

    const handleGenerateDraft = async (e) => {
        e.preventDefault();

        if (!form.industry.trim()) {
            setError("Industry is required.");
            return;
        }

        try {
            setGenerating(true);
            setMessage("");
            setError("");

            await generateVerticalSeedDraft({
                industry: form.industry.trim(),
                subIndustry: form.subIndustry?.trim() || null,
                primaryGoal: form.primaryGoal?.trim() || null,
                toneOfVoice: form.toneOfVoice?.trim() || null,
                brandStyle: form.brandStyle?.trim() || null,
                themeKey: form.themeKey?.trim() || null
            });

            setMessage("Vertical seed draft generated successfully.");
            await loadSeeds();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.response?.data ||
                "Failed to generate vertical seed draft."
            );
        } finally {
            setGenerating(false);
        }
    };

    const handleApprove = async (seedId) => {
        try {
            setApprovingId(seedId);
            setMessage("");
            setError("");

            await approveVerticalSeed(seedId);

            setMessage("Vertical seed approved and activated.");
            await loadSeeds();
        } catch (err) {
            console.error(err);
            setError("Failed to approve vertical seed.");
        } finally {
            setApprovingId(null);
        }
    };

    const handleMaterializeAllImages = async () => {
        try {
            setMaterializingAll(true);
            setMessage("");
            setError("");

            const result = await materializeAllSeedImages();

            setMessage(
                `Seed image materialization completed. Uploaded: ${result?.uploaded ?? 0}`
            );

            await loadSeeds();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.response?.data ||
                "Failed to materialize all seed images."
            );
        } finally {
            setMaterializingAll(false);
        }
    };

    const handleMaterializeSeedImages = async (seedId) => {
        if (!seedId) return;

        try {
            setMaterializingSeedId(seedId);
            setMessage("");
            setError("");

            const result = await materializeSeedImages(seedId);

            setMessage(
                `Seed ${seedId} materialized successfully. Uploaded: ${result?.uploaded ?? 0}`
            );

            await loadSeeds();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.response?.data ||
                "Failed to materialize selected seed images."
            );
        } finally {
            setMaterializingSeedId(null);
        }
    };

    const activeSeeds = useMemo(() => seeds.filter((x) => x.isActive).length, [seeds]);

    const draftSeeds = useMemo(
        () => seeds.filter((x) => normalizeSeedStatus(x.status) === "draft").length,
        [seeds]
    );

    const archivedSeeds = useMemo(
        () => seeds.filter((x) => normalizeSeedStatus(x.status) === "archived").length,
        [seeds]
    );

    const totalModules = useMemo(
        () => seeds.reduce((sum, seed) => sum + (seed.modules?.length || 0), 0),
        [seeds]
    );

    const avgQuality = useMemo(() => {
        if (!seeds.length) return 0;
        return Math.round(seeds.reduce((sum, seed) => sum + (seed.qualityScore || 0), 0) / seeds.length);
    }, [seeds]);

    const uniqueIndustries = useMemo(() => {
        return Array.from(
            new Set(seeds.map((seed) => seed.industry).filter(Boolean))
        ).sort();
    }, [seeds]);

    const uniqueTemplates = useMemo(() => {
        return Array.from(
            new Set(seeds.map((seed) => seed.templatePackKey).filter(Boolean))
        ).sort();
    }, [seeds]);

    const filteredSeeds = useMemo(() => {
        const query = seedSearch.trim().toLowerCase();

        return seeds.filter((seed) => {
            const status = normalizeSeedStatus(seed.status);

            if (query && !buildSeedSearchText(seed).includes(query)) {
                return false;
            }

            if (seedStatusFilter !== "all" && status !== seedStatusFilter) {
                return false;
            }

            if (seedActiveFilter === "active" && !seed.isActive) {
                return false;
            }

            if (seedActiveFilter === "inactive" && seed.isActive) {
                return false;
            }

            if (seedIndustryFilter !== "all" && seed.industry !== seedIndustryFilter) {
                return false;
            }

            if (seedTemplateFilter !== "all" && seed.templatePackKey !== seedTemplateFilter) {
                return false;
            }

            return true;
        });
    }, [
        seeds,
        seedSearch,
        seedStatusFilter,
        seedActiveFilter,
        seedIndustryFilter,
        seedTemplateFilter
    ]);

    const totalSeedPages = useMemo(() => {
        return Math.max(1, Math.ceil(filteredSeeds.length / seedPageSize));
    }, [filteredSeeds.length, seedPageSize]);

    const pagedSeeds = useMemo(() => {
        const safePage = Math.min(seedPage, totalSeedPages);
        const start = (safePage - 1) * seedPageSize;
        return filteredSeeds.slice(start, start + seedPageSize);
    }, [filteredSeeds, seedPage, seedPageSize, totalSeedPages]);


    const healthScore =
        intelligenceRows.length === 0
            ? 0
            : Math.round(
                intelligenceRows.filter(x => x.isMatched).length
                * 100
                / intelligenceRows.length
            );

    useEffect(() => {
        setSeedPage(1);
    }, [
        seedSearch,
        seedStatusFilter,
        seedActiveFilter,
        seedTemplateFilter,
        seedIndustryFilter,
        seedPageSize
    ]);

    useEffect(() => {
        if (seedPage > totalSeedPages) {
            setSeedPage(totalSeedPages);
        }
    }, [seedPage, totalSeedPages]);

    if (!isSuperAdmin) {
        return (
            <PageShell title="Vertical Intelligence Center">
                <div className="crm-card">
                    <h3>Access denied</h3>
                    <p>Only super admin users can manage vertical seeds.</p>
                </div>
            </PageShell>
        );
    }

    return (
        <PageShell title="Vertical Intelligence Center">
            <div className="seed-page-scroll">
                <div className="seed-page">
                    <div className="intelligence-tabs">
                        <button
                            className={activeTab === "seeds" ? "active" : ""}
                            onClick={() => setActiveTab("seeds")}
                        >
                            Seeds
                        </button>

                        <button
                            className={activeTab === "blueprints" ? "active" : ""}
                            onClick={() => {
                                setActiveTab("blueprints");
                                loadBlueprints();
                            }}
                        >
                            Blueprints
                        </button>

                        <button
                            className={activeTab === "intelligence" ? "active" : ""}
                            onClick={() => {
                                setActiveTab("intelligence");
                                loadIntelligence();
                            }}
                        >
                            Intelligence
                        </button>

                        <button
                            className={activeTab === "learning" ? "active" : ""}
                            onClick={() => setActiveTab("learning")}
                        >
                            Learning
                        </button>

                        <button
                            className={activeTab === "testlab" ? "active" : ""}
                            onClick={() => setActiveTab("testlab")}
                        >
                            Test Lab
                        </button>
                    </div>
                    {(message || error) && (
                        <div className="seed-message-stack">
                            {message && <div className="crm-alert crm-alert-success">{message}</div>}
                            {error && <div className="crm-alert crm-alert-error">{error}</div>}
                        </div>
                    )}
                    {activeTab === "seeds" && (
                        <>
                            <div className="seed-hero">
                                <div className="seed-hero-copy">
                                    <span className="seed-kicker">AI Seed Control Center</span>
                                    <h1>Vertical Intelligence Center</h1>
                                    <p>
                                        Generate, inspect, approve, learn, and materialize reusable vertical website blueprints
                                        across all industries.
                                    </p>
                                </div>

                                <div className="seed-hero-actions">
                                    <button className="crm-btn crm-btn-secondary" onClick={loadSeeds} disabled={loading}>
                                        {loading ? "Refreshing..." : "Refresh"}
                                    </button>

                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        type="button"
                                        onClick={handleMaterializeAllImages}
                                        disabled={materializingAll}
                                    >
                                        {materializingAll ? "Materializing..." : "Materialize All Images"}
                                    </button>

                                    <button
                                        className="crm-btn crm-btn-primary"
                                        type="button"
                                        onClick={handleRunBatch}
                                        disabled={batchRunning}
                                    >
                                        {batchRunning ? "Running Batch..." : "Run Auto Seed Batch"}
                                    </button>
                                </div>
                            </div>

                            <div className="seed-stat-grid">
                                <div className="seed-stat-card">
                                    <span>Total Seeds</span>
                                    <strong>{seeds.length}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Filtered</span>
                                    <strong>{filteredSeeds.length}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Active</span>
                                    <strong>{activeSeeds}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Drafts</span>
                                    <strong>{draftSeeds}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Archived</span>
                                    <strong>{archivedSeeds}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Total Modules</span>
                                    <strong>{totalModules}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Avg Quality</span>
                                    <strong>{avgQuality}</strong>
                                </div>
                            </div>

                            <div className="seed-top-grid">
                                <div className="crm-card seed-panel-card">
                                    <div className="seed-panel-head">
                                        <div>
                                            <span className="seed-kicker">Single Seed Generator</span>
                                            <h3>Generate AI Seed Draft</h3>
                                            <p>Create one reusable vertical blueprint with AI-generated website and CRM seed data.</p>
                                        </div>
                                    </div>

                                    <form onSubmit={handleGenerateDraft} className="seed-form">
                                        <div className="seed-form-grid">
                                            {[
                                                ["industry", "Industry", "Education"],
                                                ["subIndustry", "Sub Industry", "Sports Academy"],
                                                ["primaryGoal", "Primary Goal", "Generate Leads"],
                                                ["toneOfVoice", "Tone of Voice", "Professional"],
                                                ["brandStyle", "Brand Style", "Modern"],
                                                ["themeKey", "Theme Key", "clean-light"]
                                            ].map(([field, label, placeholder]) => (
                                                <div className="seed-input-group" key={field}>
                                                    <label>{label}</label>
                                                    <input
                                                        value={form[field]}
                                                        onChange={(e) => updateField(field, e.target.value)}
                                                        placeholder={placeholder}
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        <div className="seed-form-footer">
                                            <button className="crm-btn crm-btn-primary seed-submit-btn" type="submit" disabled={generating}>
                                                {generating ? "Generating Seed..." : "Generate Draft"}
                                            </button>
                                        </div>
                                    </form>
                                </div>

                                <div className="crm-card seed-panel-card">
                                    <div className="seed-panel-head">
                                        <div>
                                            <span className="seed-kicker">Lifecycle</span>
                                            <h3>Seed Strategy</h3>
                                            <p>
                                                Draft seeds stay inactive until approved. Approving a seed retires the older active seed
                                                for the same vertical key.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="crm-seed-flow">
                                        <div>AI Draft</div>
                                        <span>→</span>
                                        <div>Admin Review</div>
                                        <span>→</span>
                                        <div>Active Seed</div>
                                    </div>

                                    <div className="seed-side-actions">
                                        <div className="seed-info-block">
                                            <h4>Image Materialization</h4>
                                            <p>
                                                Upload seed image URLs to Nexus Blob Storage so generated sites use your controlled media URLs.
                                            </p>
                                        </div>

                                        <button
                                            className="crm-btn crm-btn-secondary"
                                            type="button"
                                            onClick={handleMaterializeAllImages}
                                            disabled={materializingAll}
                                        >
                                            {materializingAll ? "Materializing..." : "Materialize All Seed Images"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="crm-card seed-library-card">
                                <div className="seed-library-header">
                                    <div>
                                        <span className="seed-kicker">Seed Library</span>
                                        <h3>Vertical Seed Registry</h3>
                                        <p className="crm-muted">
                                            Search, filter, inspect, approve, materialize images, and generate test sites from reusable vertical seeds.
                                        </p>
                                    </div>

                                    <div className="seed-library-summary">
                                        <strong>{filteredSeeds.length}</strong>
                                        <span>matching seeds</span>
                                    </div>
                                </div>

                                <div className="seed-filter-panel">
                                    <div className="seed-search-box">
                                        <label>Search Seeds</label>
                                        <input
                                            value={seedSearch}
                                            onChange={(e) => setSeedSearch(e.target.value)}
                                            placeholder="Search industry, template, seed key, module..."
                                        />
                                    </div>

                                    <div className="seed-filter-grid">
                                        <label>
                                            <span>Industry</span>
                                            <select value={seedIndustryFilter} onChange={(e) => setSeedIndustryFilter(e.target.value)}>
                                                <option value="all">All industries</option>
                                                {uniqueIndustries.map((industry) => (
                                                    <option key={industry} value={industry}>
                                                        {industry}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label>
                                            <span>Template</span>
                                            <select value={seedTemplateFilter} onChange={(e) => setSeedTemplateFilter(e.target.value)}>
                                                <option value="all">All templates</option>
                                                {uniqueTemplates.map((template) => (
                                                    <option key={template} value={template}>
                                                        {template}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label>
                                            <span>Status</span>
                                            <select value={seedStatusFilter} onChange={(e) => setSeedStatusFilter(e.target.value)}>
                                                <option value="all">All statuses</option>
                                                <option value="active">Active</option>
                                                <option value="draft">Draft</option>
                                                <option value="archived">Archived</option>
                                            </select>
                                        </label>

                                        <label>
                                            <span>Activation</span>
                                            <select value={seedActiveFilter} onChange={(e) => setSeedActiveFilter(e.target.value)}>
                                                <option value="all">All</option>
                                                <option value="active">Active only</option>
                                                <option value="inactive">Inactive only</option>
                                            </select>
                                        </label>

                                        <label>
                                            <span>Rows</span>
                                            <select
                                                value={seedPageSize}
                                                onChange={(e) => setSeedPageSize(Number(e.target.value))}
                                            >
                                                <option value={10}>10 rows</option>
                                                <option value={20}>20 rows</option>
                                                <option value={50}>50 rows</option>
                                            </select>
                                        </label>
                                    </div>

                                    <div className="seed-filter-actions">
                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            onClick={() => {
                                                setSeedSearch("");
                                                setSeedStatusFilter("all");
                                                setSeedActiveFilter("all");
                                                setSeedTemplateFilter("all");
                                                setSeedIndustryFilter("all");
                                                setSeedPageSize(10);
                                                setSeedPage(1);
                                            }}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>

                                <div className="seed-table-toolbar">
                                    <div>
                                        Showing{" "}
                                        <strong>
                                            {filteredSeeds.length === 0 ? 0 : (seedPage - 1) * seedPageSize + 1}
                                        </strong>
                                        {" "}-
                                        <strong>
                                            {Math.min(seedPage * seedPageSize, filteredSeeds.length)}
                                        </strong>
                                        {" "}of <strong>{filteredSeeds.length}</strong>
                                    </div>

                                    <div className="seed-pagination">
                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage <= 1}
                                            onClick={() => setSeedPage((prev) => Math.max(1, prev - 1))}
                                        >
                                            Previous
                                        </button>

                                        <span>
                                            Page <strong>{seedPage}</strong> of <strong>{totalSeedPages}</strong>
                                        </span>

                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage >= totalSeedPages}
                                            onClick={() => setSeedPage((prev) => Math.min(totalSeedPages, prev + 1))}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                                <div className="seed-table-shell">
                                    <table className="seed-admin-table">
                                        <thead>
                                            <tr>
                                                <th>Seed</th>
                                                <th>Vertical</th>
                                                <th>Renderer</th>
                                                <th>Quality</th>
                                                <th>Status</th>
                                                <th>Images</th>
                                                <th>Modules</th>
                                                <th>Created</th>
                                                <th className="seed-action-col">Actions</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {pagedSeeds.length === 0 && (
                                                <tr>
                                                    <td colSpan="9">
                                                        <div className="seed-empty-state">
                                                            No seeds match the current filters.
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}

                                            {pagedSeeds.map((seed) => (
                                                <tr
                                                    key={seed.id}
                                                    className={selectedSeed?.id === seed.id ? "is-selected" : ""}
                                                    onClick={() => setSelectedSeed(seed)}
                                                >
                                                    <td>
                                                        <div className="seed-main-cell">
                                                            <strong>#{seed.id}</strong>
                                                            <span>{seed.seedKey}</span>
                                                            <small>v{seed.version}</small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="seed-vertical-cell">
                                                            <strong>{seed.industry || "-"}</strong>
                                                            <span>{seed.subIndustry || "-"}</span>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="seed-renderer-cell">
                                                            <span>{seed.templatePackKey || "-"}</span>
                                                            <small>{seed.pattern || "-"}</small>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="seed-score-cell">
                                                            <strong>{seed.qualityScore ?? 0}</strong>
                                                            <div className="seed-score-track">
                                                                <span style={{ width: `${Math.min(100, seed.qualityScore || 0)}%` }} />
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="seed-status-stack">
                                                            <span className={`seed-status-pill ${getSeedStatusClass(seed.status)}`}>
                                                                {getSeedStatusLabel(seed.status)}
                                                            </span>

                                                            {seed.isActive ? (
                                                                <span className="seed-mini-pill active">Active</span>
                                                            ) : (
                                                                <span className="seed-mini-pill inactive">Inactive</span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {seed.hasBlobImageUrls ? (
                                                            <span className="seed-image-pill ready">Blob Ready</span>
                                                        ) : seed.hasUploadableImageUrls ? (
                                                            <span className="seed-image-pill uploadable">Uploadable</span>
                                                        ) : seed.hasImageSeedJson ? (
                                                            <span className="seed-image-pill empty">No URLs</span>
                                                        ) : (
                                                            <span className="seed-image-pill empty">No Seed</span>
                                                        )}
                                                    </td>

                                                    <td>
                                                        <div className="seed-module-pill-row">
                                                            {(seed.modules || []).slice(0, 3).map((module) => (
                                                                <span key={module.key} title={module.displayName || module.key}>
                                                                    {getModuleIcon(module.key)} {module.key}
                                                                </span>
                                                            ))}

                                                            {(seed.modules || []).length > 3 && (
                                                                <span>+{seed.modules.length - 3}</span>
                                                            )}

                                                            {(seed.modules || []).length === 0 && (
                                                                <span>No modules</span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <div className="seed-date-cell">
                                                            {formatDate(seed.createdAtUtc)}
                                                        </div>
                                                    </td>

                                                    <td className="seed-action-col">
                                                        <div className="seed-row-actions">
                                                            <button
                                                                className="crm-btn crm-btn-small crm-btn-secondary"
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedSeed(seed);
                                                                }}
                                                            >
                                                                Inspect
                                                            </button>

                                                            <button
                                                                className="crm-btn crm-btn-small crm-btn-secondary"
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMaterializeSeedImages(seed.id);
                                                                }}
                                                                disabled={materializingSeedId === seed.id}
                                                            >
                                                                {materializingSeedId === seed.id ? "..." : "Images"}
                                                            </button>

                                                            {!seed.isActive && normalizeSeedStatus(seed.status) !== "active" ? (
                                                                <button
                                                                    className="crm-btn crm-btn-small crm-btn-primary"
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleApprove(seed.id);
                                                                    }}
                                                                    disabled={approvingId === seed.id}
                                                                >
                                                                    {approvingId === seed.id ? "..." : "Approve"}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="crm-btn crm-btn-small crm-btn-secondary"
                                                                    type="button"
                                                                    disabled={testGeneratingSeedId === seed.id}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleGenerateSeedTestSites(seed);
                                                                    }}
                                                                >
                                                                    {testGeneratingSeedId === seed.id ? "..." : "Test"}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="seed-table-footer">
                                    <div>
                                        Total: <strong>{seeds.length}</strong> · Filtered: <strong>{filteredSeeds.length}</strong>
                                    </div>

                                    <div className="seed-pagination">
                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage <= 1}
                                            onClick={() => setSeedPage(1)}
                                        >
                                            First
                                        </button>

                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage <= 1}
                                            onClick={() => setSeedPage((prev) => Math.max(1, prev - 1))}
                                        >
                                            Previous
                                        </button>

                                        <span>
                                            Page <strong>{seedPage}</strong> / <strong>{totalSeedPages}</strong>
                                        </span>

                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage >= totalSeedPages}
                                            onClick={() => setSeedPage((prev) => Math.min(totalSeedPages, prev + 1))}
                                        >
                                            Next
                                        </button>

                                        <button
                                            type="button"
                                            className="crm-btn crm-btn-small crm-btn-secondary"
                                            disabled={seedPage >= totalSeedPages}
                                            onClick={() => setSeedPage(totalSeedPages)}
                                        >
                                            Last
                                        </button>
                                    </div>
                                </div>

                                {batchResult?.items?.length > 0 && (
                                    <div className="crm-card seed-batch-result-card">
                                        <h3>Batch Result</h3>

                                        <div className="crm-table-wrap">
                                            <table className="crm-table">
                                                <thead>
                                                    <tr>
                                                        <th>Industry</th>
                                                        <th>Sub Industry</th>
                                                        <th>Status</th>
                                                        <th>Seed ID</th>
                                                        <th>Error</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {batchResult.items.map((item, index) => (
                                                        <tr key={`${item.industry}-${item.subIndustry}-${index}`}>
                                                            <td>{item.industry}</td>
                                                            <td>{item.subIndustry}</td>
                                                            <td>{item.status}</td>
                                                            <td>{item.seedId || "-"}</td>
                                                            <td>{item.error || "-"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {activeTab === "learning" && (
                        <div className="crm-card seed-learning-card">
                            <div className="crm-card-header seed-learning-header">
                                <div>
                                    <span className="seed-kicker">Seed Auto-Learning</span>
                                    <h3>Improvement Recommendations</h3>
                                    <p className="crm-muted">
                                        Nexus learns from missing seeds, CRM edits, image replacements, and quality scans.
                                    </p>
                                </div>

                                <div className="seed-learning-actions">
                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        type="button"
                                        onClick={handleRunLearning}
                                        disabled={learningRunning}
                                    >
                                        {learningRunning ? "Analyzing..." : "Run Learning"}
                                    </button>

                                    <button
                                        type="button"
                                        className="crm-btn crm-btn-primary"
                                        disabled={!selectedSeed?.id || testGeneratingSeedId === selectedSeed?.id}
                                        onClick={() => {
                                            if (!selectedSeed?.id) return;
                                            handleGenerateSeedTestSites(selectedSeed);
                                        }}
                                    >
                                        {selectedSeed?.id && testGeneratingSeedId === selectedSeed.id
                                            ? "Generating Test Sites..."
                                            : "Generate Test Sites"}
                                    </button>
                                </div>
                            </div>

                            {recommendations.length === 0 ? (
                                <div className="seed-empty-state">No seed learning recommendations yet.</div>
                            ) : (
                                <div className="seed-recommendation-grid">
                                    {recommendations.map((item) => (
                                        <div className="seed-recommendation-card" key={item.id}>
                                            <div className="seed-recommendation-top">
                                                <div>
                                                    <div className="seed-recommendation-title">
                                                        {item.subIndustry || item.industry}
                                                    </div>
                                                    <div className="seed-recommendation-subtitle">
                                                        {item.industry}{item.subIndustry ? ` · ${item.subIndustry}` : ""}
                                                    </div>
                                                </div>

                                                <span className={`seed-priority-chip ${getPriorityClass(item.priority)}`}>
                                                    {item.priority}
                                                </span>
                                            </div>

                                            <p className="seed-recommendation-summary">
                                                {item.summary || "No summary available."}
                                            </p>

                                            <div className="seed-recommendation-meta">
                                                <div>
                                                    <span>Status</span>
                                                    <strong>{getRecommendationStatusLabel(item.status)}</strong>
                                                </div>
                                                <div>
                                                    <span>Signals</span>
                                                    <strong>{item.totalSignals ?? 0}</strong>
                                                </div>
                                            </div>

                                            <div className="seed-recommendation-footer">
                                                {item.createdSeedId ? (
                                                    <button
                                                        className="crm-btn crm-btn-small crm-btn-secondary"
                                                        type="button"
                                                        onClick={() => {
                                                            const match = seeds.find(
                                                                (seed) => String(seed.id) === String(item.createdSeedId)
                                                            );

                                                            if (match) {
                                                                setSelectedSeed(match);
                                                                return;
                                                            }

                                                            window.history.pushState(
                                                                null,
                                                                "",
                                                                `/vertical-seeds?seedId=${item.createdSeedId}`
                                                            );

                                                            loadSeeds();
                                                        }}
                                                    >
                                                        Open Draft Seed
                                                    </button>
                                                ) : (
                                                    <button
                                                        className="crm-btn crm-btn-small crm-btn-primary"
                                                        type="button"
                                                        disabled={generatingSeedId === item.id || item.status === "Reviewed"}
                                                        onClick={() => handleGenerateSeedDraftFromRecommendation(item.id)}
                                                    >
                                                        {item.status === "Reviewed" ? "Draft Created" : "Generate vNext"}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "blueprints" && (
                        <div className="crm-card">

                            <div className="seed-library-header">
                                <div>
                                    <span className="seed-kicker">Blueprint Registry</span>
                                    <h3>V2 Blueprint Definitions</h3>
                                </div>
                            </div>

                            <div className="seed-filter-panel">
                                <div className="seed-search-box">
                                    <label>Search Blueprints</label>

                                    <input
                                        value={blueprintSearch}
                                        onChange={(e) => setBlueprintSearch(e.target.value)}
                                        placeholder="Fitness, Healthcare, Ecommerce..."
                                    />
                                </div>
                            </div>

                            {blueprintLoading ? (
                                <div className="seed-empty-state">
                                    Loading blueprints...
                                </div>
                            ) : (
                                <table className="seed-admin-table">
                                    <thead>
                                        <tr>
                                            <th>Vertical</th>
                                            <th>Industry</th>
                                            <th>Status</th>
                                            <th>Version</th>
                                            <th></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredBlueprints.map(bp => (
                                            <tr key={bp.id}>
                                                <td>{bp.verticalKey}</td>
                                                <td>{bp.industry}</td>
                                                <td>{bp.status}</td>
                                                <td>v{bp.version}</td>

                                                <td>
                                                    <button
                                                        className="crm-btn crm-btn-small crm-btn-secondary"
                                                        onClick={async () => {
                                                            const detail = await getBlueprint(bp.id);
                                                            setSelectedBlueprint(detail);
                                                        }}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === "intelligence" && (
                        <div className="crm-card">

                            <div className="seed-library-header">
                                <div>
                                    <span className="seed-kicker">Seed ↔ Blueprint Mapping</span>
                                    <h3>Vertical Intelligence</h3>
                                </div>
                            </div>

                            {intelligenceLoading ? (
                                <div className="seed-empty-state">
                                    Loading intelligence...
                                </div>
                            ) : (
                                <table className="seed-admin-table">
                                    <thead>
                                        <tr>
                                            <th>Seed</th>
                                            <th>Blueprint</th>
                                            <th>Status</th>
                                            <th>Seed Modules</th>
                                            <th>Blueprint Modules</th>
                                            <th>Missing In Blueprint</th>
                                            <th>Missing In Seed</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {intelligenceRows.map(row => (
                                            <tr key={row.seedId}>
                                                <td>{row.seedKey}</td>

                                                <td>
                                                    {row.blueprintVerticalKey}
                                                    <br />
                                                    <small>v{row.blueprintVersion}</small>
                                                </td>

                                                <td>
                                                    {row.isMatched
                                                        ? "✅ MATCHED"
                                                        : "⚠️ WARNING"}
                                                </td>

                                                <td>
                                                    {(row.seedModules || []).join(", ")}
                                                </td>

                                                <td>
                                                    {(row.blueprintModules || []).join(", ")}
                                                </td>

                                                <td>
                                                    {(row.missingInBlueprint || []).join(", ")}
                                                </td>

                                                <td>
                                                    {(row.missingInSeed || []).join(", ")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}


                    {activeTab === "testlab" && (
                        <div className="crm-card">

                            <div className="seed-library-header">
                                <div>
                                    <span className="seed-kicker">Blueprint Validation</span>
                                    <h3>V2 Test Lab</h3>
                                    <p className="crm-muted">
                                        Validate Seed ↔ Blueprint alignment before production.
                                    </p>
                                </div>
                            </div>

                            <div className="seed-stat-grid">

                                <div className="seed-stat-card">
                                    <span>Total Seeds</span>
                                    <strong>{seeds.length}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Platform Health</span>
                                    <strong>{healthScore}%</strong>
                                </div>


                                <div className="seed-stat-card">
                                    <span>Total Blueprints</span>
                                    <strong>{blueprints.length}</strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Mapped</span>
                                    <strong>
                                        {intelligenceRows.filter(x => x.isMatched).length}
                                    </strong>
                                </div>

                                <div className="seed-stat-card">
                                    <span>Warnings</span>
                                    <strong>
                                        {intelligenceRows.filter(x => !x.isMatched).length}
                                    </strong>
                                </div>

                                <div className="seed-learning-actions">

                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        onClick={handleRunLearning}
                                    >
                                        Run Learning
                                    </button>

                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        onClick={loadIntelligence}
                                    >
                                        Refresh Intelligence
                                    </button>

                                    <button
                                        className="crm-btn crm-btn-primary"
                                        onClick={loadBlueprints}
                                    >
                                        Refresh Blueprints
                                    </button>

                                </div>

                            </div>

                            <table className="seed-admin-table">
                                <thead>
                                    <tr>
                                        <th>Industry</th>
                                        <th>Seed</th>
                                        <th>Blueprint</th>
                                        <th>Status</th>
                                        <th>Modules</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>

                                    {intelligenceRows.map(row => (
                                        <tr key={row.seedId}>
                                            <td>
                                                {row.industry}
                                            </td>

                                            <td>
                                                {row.seedKey}
                                            </td>

                                            <td>
                                                {row.blueprintVerticalKey}
                                            </td>

                                            <td>
                                                {row.isMatched
                                                    ? "✅ Ready"
                                                    : "⚠️ Review"}
                                            </td>

                                            <td>
                                                {row.seedModules?.length || 0}
                                                {" / "}
                                                {row.blueprintModules?.length || 0}
                                            </td>
                                            <td>
                                                <button
                                                    className="crm-btn crm-btn-small crm-btn-secondary"
                                                    onClick={() => {
                                                        const seed = seeds.find(
                                                            x => x.id === row.seedId
                                                        );

                                                        if (seed)
                                                            setSelectedSeed(seed);
                                                    }}
                                                >
                                                    Open Seed
                                                </button>

                                                <button
                                                    className="crm-btn crm-btn-small crm-btn-secondary"
                                                    onClick={async () => {

                                                        if (!row.blueprintId)
                                                            return;

                                                        const detail =
                                                            await getBlueprint(
                                                                row.blueprintId
                                                            );

                                                        setSelectedBlueprint(detail);
                                                    }}
                                                >
                                                    Open Blueprint
                                                </button>

                                            </td>
                                        </tr>
                                    ))}

                                </tbody>
                            </table>

                        </div>
                    )}

                    {selectedBlueprint && (
                        <div
                            className="seed-drawer-backdrop"
                            onClick={() => setSelectedBlueprint(null)}
                        >
                            <aside
                                className="seed-drawer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="seed-drawer-header">
                                    <h2>{selectedBlueprint.verticalKey}</h2>

                                    <button
                                        className="seed-drawer-close"
                                        onClick={() => setSelectedBlueprint(null)}
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="seed-drawer-stats">

                                    <div className="seed-drawer-stat">
                                        <strong>{selectedBlueprint.version}</strong>
                                        <span>Version</span>
                                    </div>

                                    <div className="seed-drawer-stat">
                                        <strong>{selectedBlueprint.status}</strong>
                                        <span>Status</span>
                                    </div>

                                    <div className="seed-drawer-stat">
                                        <strong>{selectedBlueprint.industry}</strong>
                                        <span>Industry</span>
                                    </div>

                                </div>

                                <textarea
                                    readOnly
                                    style={{
                                        width: "100%",
                                        minHeight: "700px",
                                        fontFamily: "monospace"
                                    }}
                                    value={selectedBlueprint.blueprintJson || ""}
                                />
                            </aside>
                        </div>
                    )}

                    {selectedSeed && (
                        <div className="seed-drawer-backdrop" onClick={() => setSelectedSeed(null)}>
                            <aside className="seed-drawer" onClick={(e) => e.stopPropagation()}>
                                <div className="seed-drawer-header">
                                    <div>
                                        <span className="seed-drawer-kicker">Vertical Seed</span>
                                        <h2>{selectedSeed.subIndustry || selectedSeed.industry}</h2>
                                        <p>
                                            {selectedSeed.industry} · {selectedSeed.templatePackKey} · {selectedSeed.pattern}
                                        </p>
                                    </div>

                                    <button className="seed-drawer-close" onClick={() => setSelectedSeed(null)}>
                                        ×
                                    </button>
                                </div>

                                <div className="seed-drawer-stats">
                                    <div className="seed-drawer-stat">
                                        <strong>{selectedSeed.qualityScore || 0}</strong>
                                        <span>Quality</span>
                                    </div>
                                    <div className="seed-drawer-stat">
                                        <strong>v{selectedSeed.version}</strong>
                                        <span>Version</span>
                                    </div>
                                    <div className="seed-drawer-stat">
                                        <strong>{selectedSeed.modules?.length || 0}</strong>
                                        <span>Modules</span>
                                    </div>
                                </div>

                                <div className="seed-drawer-body">
                                    <div className="seed-detail-card">
                                        <label>Seed Key</label>
                                        <p>{selectedSeed.seedKey}</p>
                                    </div>

                                    <div className="seed-detail-card-grid">
                                        <div className="seed-detail-card">
                                            <label>Status</label>
                                            <p>{selectedSeed.status}</p>
                                        </div>

                                        <div className="seed-detail-card">
                                            <label>Active</label>
                                            <p>{selectedSeed.isActive ? "Yes" : "No"}</p>
                                        </div>

                                        <div className="seed-detail-card">
                                            <label>Template Pack</label>
                                            <p>{selectedSeed.templatePackKey || "-"}</p>
                                        </div>

                                        <div className="seed-detail-card">
                                            <label>Created</label>
                                            <p>{formatDate(selectedSeed.createdAtUtc)}</p>
                                        </div>
                                    </div>

                                    <div className="seed-section-header">
                                        <h3 className="seed-section-title">Available CRM Modules</h3>
                                        <span className="crm-muted">{selectedSeed.modules?.length || 0} modules</span>
                                    </div>

                                    {selectedSeed.modules?.length > 0 ? (
                                        <div className="seed-module-grid">
                                            {selectedSeed.modules.map((m) => (
                                                <div className="seed-module-card" key={m.key}>
                                                    <div className="seed-module-card-top">
                                                        <div className="seed-module-icon">{getModuleIcon(m.key)}</div>
                                                        <div className="seed-module-copy">
                                                            <strong>{m.displayName || m.key}</strong>
                                                            <p>{m.entityType || "entity"} · {m.key}</p>
                                                        </div>
                                                    </div>

                                                    <div className="seed-module-counts">
                                                        <span>{m.fieldCount} fields</span>
                                                        <span>{m.sampleRecordCount} records</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="seed-empty-state">
                                            No module blueprint found for this seed.
                                        </div>
                                    )}
                                </div>

                                <div className="seed-drawer-footer">
                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        type="button"
                                        onClick={() => handleMaterializeSeedImages(selectedSeed.id)}
                                        disabled={materializingSeedId === selectedSeed.id}
                                    >
                                        {materializingSeedId === selectedSeed.id ? "Materializing..." : "Materialize Images"}
                                    </button>

                                    {!selectedSeed.isActive && String(selectedSeed.status).toLowerCase() !== "active" ? (
                                        <button
                                            className="crm-btn crm-btn-primary"
                                            onClick={() => handleApprove(selectedSeed.id)}
                                            disabled={approvingId === selectedSeed.id}
                                        >
                                            {approvingId === selectedSeed.id ? "Approving..." : "Approve Seed"}
                                        </button>
                                    ) : (
                                        <span className="crm-badge crm-badge-active">Active Seed</span>
                                    )}

                                    <button
                                        className="crm-btn crm-btn-secondary"
                                        type="button"
                                        disabled={testGeneratingSeedId === selectedSeed.id}
                                        onClick={() => handleGenerateSeedTestSites(selectedSeed)}
                                    >
                                        {testGeneratingSeedId === selectedSeed.id ? "Generating..." : "Generate Test Sites"}
                                    </button>
                                </div>
                            </aside>
                        </div>
                    )}
                </div>


            </div>
        </PageShell>
    );
}